import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';

export const buildKeySet = (symbols: SymbolKey[], isForwardMode: boolean): Set<string> =>
  new Set(symbols.map(s => symbolKey(s, isForwardMode)));

export const filterByKey = (
  symbols: SymbolKey[],
  excludeKeys: Set<string>,
  isForwardMode: boolean
): SymbolKey[] => symbols.filter(s => !excludeKeys.has(symbolKey(s, isForwardMode)));

export const buildIndexByKey = (
  symbols: SymbolKey[],
  isForwardMode: boolean
): Map<string, number> => {
  const index = new Map<string, number>();
  for (let i = 0; i < symbols.length; i++) {
    index.set(symbolKey(symbols[i], isForwardMode), i);
  }
  return index;
};

export type ToggleChanges = {
  toRemove: Set<number>;
  toAdd: SymbolKey[];
  limitReached: boolean;
};

export const computeToggleChanges = (
  currentSymbols: SymbolKey[],
  incomingSymbols: SymbolKey[],
  isForwardMode: boolean,
  maxSymbols: number
): ToggleChanges => {
  const existingKeys = buildKeySet(currentSymbols, isForwardMode);
  const indexByKey = buildIndexByKey(currentSymbols, isForwardMode);
  const toRemove = new Set<number>();
  const toAdd: SymbolKey[] = [];
  let limitReached = false;

  for (const sym of incomingSymbols) {
    const k = symbolKey(sym, isForwardMode);
    if (existingKeys.has(k)) {
      const idx = indexByKey.get(k);
      if (idx != null) toRemove.add(idx);
      continue;
    }
    if (currentSymbols.length - toRemove.size + toAdd.length >= maxSymbols) {
      limitReached = true;
      continue;
    }
    toAdd.push(sym);
  }

  return { toRemove, toAdd, limitReached };
};

export const applyToggleChanges = (
  currentSymbols: SymbolKey[],
  toRemove: Set<number>,
  toAdd: SymbolKey[]
): SymbolKey[] => {
  const next: SymbolKey[] = [];
  for (let i = 0; i < currentSymbols.length; i++) {
    if (!toRemove.has(i)) next.push(currentSymbols[i]);
  }
  return [...toAdd, ...next];
};
